// The pictures on a message in a thread: thumbnails in the bubble, one tap to
// see a picture whole. A Prime thread passes them through SessionPictures (blob
// URLs for blob ids, data: URLs for older inline base64; the CSP allows both);
// a coding agent's thread passes blob URLs (RunPictures).
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

/** onError: the URL would not draw (revoked, or a bad fetch); the owner fetches it again. */
export type MessagePicture = { media_type: string; data: string } | { url: string; onError?: () => void }

/** What the server puts in a message sent with pictures and no words (SendAsync in internal/assistant/chat.go). */
export const PICTURE_ONLY = '(see the attached image)'

const srcOf = (p: MessagePicture) => ('url' in p ? p.url : `data:${p.media_type};base64,${p.data}`)
const onErrorOf = (p: MessagePicture) => ('url' in p ? p.onError : undefined)

/** One picture whole, over everything: a tap or Escape closes it (also What's new's screenshots). */
export const PictureViewer = ({
  src,
  onError,
  onClose,
  label = 'Picture',
}: {
  src: string
  onError?: () => void
  onClose: () => void
  label?: string
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4"
      style={{
        paddingTop: 'max(1rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
      }}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={onClose}
      data-testid="message-picture-viewer"
    >
      <img src={src} onError={onError} alt="" className="max-h-full max-w-full object-contain" />
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 h-11 w-11 rounded-full bg-panel border border-line text-fg flex items-center justify-center"
        style={{ top: 'max(1rem, env(safe-area-inset-top))' }}
        aria-label="Close"
      >
        <X size={20} aria-hidden />
      </button>
    </div>,
    document.body,
  )
}

export const MessagePictures = ({ images }: { images: MessagePicture[] }) => {
  const [open, setOpen] = useState<number | null>(null)
  const shown = open === null ? undefined : images[open]
  return (
    <>
      <ul
        className="flex flex-wrap justify-end gap-1.5"
        aria-label={images.length === 1 ? 'Attached picture' : `${images.length} attached pictures`}
        data-testid="message-pictures"
      >
        {images.map((p, i) => (
          <li key={i}>
            <button
              type="button"
              onClick={() => setOpen(i)}
              className="block overflow-hidden border border-line bg-raised"
              style={{ borderRadius: 'var(--radius-card-inner)' }}
              aria-label={`Open picture ${i + 1}`}
              data-testid="message-picture"
            >
              <img
                src={srcOf(p)}
                onError={onErrorOf(p)}
                alt=""
                loading="lazy"
                decoding="async"
                className={
                  images.length === 1 ? 'block max-h-56 max-w-full w-auto' : 'block h-24 w-24 object-cover'
                }
              />
            </button>
          </li>
        ))}
      </ul>
      {shown && <PictureViewer src={srcOf(shown)} onError={onErrorOf(shown)} onClose={() => setOpen(null)} />}
    </>
  )
}
